import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { ThreadMessage } from "@rakazo/contracts";
import { isRetryableError } from "@rakazo/core";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger, Spinner } from "@rakazo/ui-web";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, CircleHelp, X } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { AskCard } from "../components/AskCard";
import type { SpaceQueries } from "../lib/query";
import type { RpcClient } from "../lib/rpc";

export type QuestionInboxItem = {
  botId: string; botName: string; groupId: string | null; groupName: string | null;
  message: ThreadMessage; context: string;
};

/** A quiet, persistent alert: new items never move focus away from the current work. */
export function QuestionInbox({ queries, rpc, onOpenQuestion }: {
  queries: SpaceQueries; rpc: RpcClient; onOpenQuestion: (item: QuestionInboxItem) => void;
}) {
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(() => new Set());
  const inbox = useInfiniteQuery(queries.threads.questions.infiniteOptions({
    input: (after: string | null) => after ? { after } : {},
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    refetchInterval: (query) => query.state.error && !isRetryableError(query.state.error) ? false : 15_000,
    refetchOnWindowFocus: true,
  }));
  // Load every page, including pages with only obsolete or inaccessible questions.
  useEffect(() => {
    if (inbox.hasNextPage && !inbox.isFetching && !inbox.isError) void inbox.fetchNextPage();
  }, [inbox.hasNextPage, inbox.isFetching, inbox.isError, inbox.fetchNextPage]);
  const byId = new Map<string, QuestionInboxItem>();
  for (const page of inbox.data?.pages ?? []) for (const item of page.items) byId.set(item.message.id, item);
  const items = inbox.isError && !isRetryableError(inbox.error) ? [] : [...byId.values()]
    .filter((item) => !answeredIds.has(item.message.id))
    .sort((a, b) => a.message.createdAt.localeCompare(b.message.createdAt) || a.message.id.localeCompare(b.message.id));
  const answer = async (item: QuestionInboxItem, text: string, username?: string, dismiss?: boolean) => {
    await rpc.threads.answer({
      ...(item.groupId ? { groupId: item.groupId } : { botId: item.botId, threadId: item.message.threadId }),
      messageId: item.message.id, runId: item.message.runId!, answer: text,
      ...(username ? { username } : {}), ...(dismiss ? { dismiss: true as const } : {}),
    });
    setAnsweredIds((previous) => new Set(previous).add(item.message.id));
    // Do not turn a successful answer into a failed submission if refresh is offline.
    void inbox.refetch();
  };
  const retire = async (item: QuestionInboxItem) => {
    await rpc.threads.retireQuestion({
      ...(item.groupId ? { groupId: item.groupId } : { botId: item.botId, threadId: item.message.threadId }),
      messageId: item.message.id, runId: item.message.runId!,
    });
    setAnsweredIds(previous => new Set(previous).add(item.message.id));
    void inbox.refetch();
  };
  return <QuestionInboxView items={items} loading={inbox.isPending || inbox.hasNextPage}
    error={inbox.isError} refreshing={inbox.isFetching} onRefresh={() => void inbox.refetch()}
    onAnswer={answer} onRetire={retire} onOpenQuestion={onOpenQuestion} />;
}

export function QuestionInboxView({ items, loading, error, refreshing, onRefresh, onAnswer, onRetire, onOpenQuestion }: {
  items: QuestionInboxItem[]; loading?: boolean; error?: boolean; refreshing?: boolean;
  onRefresh: () => void;
  onAnswer: (item: QuestionInboxItem, answer: string, username?: string, dismiss?: boolean) => Promise<void>;
  onRetire: (item: QuestionInboxItem) => Promise<void>;
  onOpenQuestion: (item: QuestionInboxItem) => void;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const count = items.reduce((sum, item) => sum + item.message.blocks.length, 0);
  const [retiringId, setRetiringId] = useState<string | null>(null);
  const [retireError, setRetireError] = useState<{ messageId: string; text: string } | null>(null);
  const botSelectId = useId();
  const [selectedBot, setSelectedBot] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [position, setPosition] = useState(0);
  const cards = [...items].sort((a, b) =>
    a.botName.localeCompare(b.botName) || a.botId.localeCompare(b.botId) ||
    a.message.createdAt.localeCompare(b.message.createdAt) || a.message.id.localeCompare(b.message.id),
  ).flatMap(item => item.message.blocks.flatMap((block, index) =>
    block.kind === "ask" || block.kind === "choice" ? [{ item, block, key: item.message.id + ":" + index }] : [],
  ));
  const groups = [...new Map(cards.map(card => [card.item.botId, { id: card.item.botId, name: card.item.botName }])).values()];
  const effectiveBot = groups.some(group => group.id === selectedBot) ? selectedBot : null;
  const navigation = effectiveBot ? cards.filter(card => card.item.botId === effectiveBot) : cards;
  const foundIndex = navigation.findIndex(card => card.key === activeKey);
  const activeIndex = foundIndex >= 0 ? foundIndex : Math.min(position, Math.max(navigation.length - 1, 0));
  const current = navigation[activeIndex];
  // Keep identity stable when polling inserts another question before this one.
  useEffect(() => { if (current) setActiveKey(current.key); }, [current?.key]);
  const retireCurrent = async () => {
    if (!current || retiringId) return;
    setPosition(activeIndex); setRetiringId(current.item.message.id); setRetireError(null);
    try { await onRetire(current.item); } catch { setRetireError({ messageId: current.item.message.id, text: t`Could not retire this question. Refresh and try again.` }); }
    finally { setRetiringId(null); }
  };
  const goTo = (index: number) => {
    const card = navigation[index];
    if (card) { setPosition(index); setActiveKey(card.key); }
  };
  const answerCurrent = async (item: QuestionInboxItem, text: string, username?: string, dismiss?: boolean) => {
    setPosition(activeIndex);
    await onAnswer(item, text, username, dismiss);
  };

  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger render={<Button variant="floating" size="icon-lg" />}
      className="app-no-drag relative shrink-0" title={t`Questions waiting for an answer`}
      data-testid="question-inbox-alert" aria-label={error ? t`Questions unavailable` : t`Questions: ${count} pending`}>
      <CircleHelp strokeWidth={1.6} className="size-[18px]" aria-hidden="true" />
      <span data-testid="question-inbox-count" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium tabular-nums text-primary-foreground" aria-live="polite" aria-atomic="true">{error ? "!" : `${count}${loading ? "+" : ""}`}</span>
    </DialogTrigger>
    <DialogContent showCloseButton={false} data-testid="question-inbox"
      className="app-no-drag flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div><DialogTitle className="text-base"><Trans>Questions</Trans></DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground"><Trans>Pending question cards from all bots in this space.</Trans></p></div>
        <DialogClose render={<Button variant="ghost" size="icon-sm" />} aria-label={t`Close`}><X size={18} /></DialogClose>
      </div>
      {cards.length ? <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <label htmlFor={botSelectId} className="text-sm text-muted-foreground"><Trans>Bot</Trans></label>
          <select id={botSelectId} value={effectiveBot ?? "all"} className="min-w-0 max-w-64 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            onChange={event => { setSelectedBot(event.target.value === "all" ? null : event.target.value); setPosition(0); setActiveKey(null); }}>
            <option value="all">{t`All bots`} ({cards.length})</option>
            {groups.map(group => <option key={group.id} value={group.id}>{group.name} ({cards.filter(card => card.item.botId === group.id).length})</option>)}
          </select>
        </div>
        <p className="text-sm tabular-nums text-muted-foreground" data-testid="question-position" aria-live="polite" aria-atomic="true"><Trans>Question {activeIndex + 1} of {navigation.length}</Trans></p>
      </div> : null}
      <div className="rk-scroll min-h-0 space-y-5 overflow-y-auto p-4" aria-busy={refreshing}>
        {error ? <div role="alert" className="flex items-center justify-between gap-3 text-sm text-destructive">
          <Trans>Could not refresh questions. This list may be out of date.</Trans>
          <Button variant="outline" size="sm" disabled={refreshing} onClick={onRefresh}><Trans>Try again</Trans></Button>
        </div> : null}
        {cards.map(({ item, block, key }) => <section key={key} hidden={current?.key !== key} data-question-id={item.message.id} className="space-y-2">
          <div className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{item.botName}{item.groupName ? ` · ${item.groupName}` : ""}</span>
            <Button variant="link" size="sm" onClick={() => { setOpen(false); onOpenQuestion(item); }}><Trans>Open conversation</Trans></Button>
          </div>
          {item.context && !(block.kind === "ask" && block.brief) ?
            <div className="px-1 text-sm text-muted-foreground"><p className="mb-1 font-medium"><Trans>Background from the conversation</Trans></p>
              <ChatMarkdown>{item.context.slice(0, 600)}</ChatMarkdown>
              {item.context.length > 600 ? <details><summary className="mt-2 cursor-pointer"><Trans>Read full background</Trans></summary><div className="mt-2 max-h-52 overflow-auto"><ChatMarkdown>{item.context}</ChatMarkdown></div></details> : null}
            </div> : null}
          {block.kind === "ask" ?
            <AskCard key={key} block={block} canAnswer canDismiss
              onAnswer={(text, username) => answerCurrent(item, text, username)} onDismiss={() => answerCurrent(item, "", undefined, true)} /> :
            block.kind === "choice" ? <div key={key} className="rounded-xl border border-border p-4">
              <ChatMarkdown>{block.question}</ChatMarkdown>
              <Button variant="link" onClick={() => { setOpen(false); onOpenQuestion(item); }}><Trans>Answer in conversation</Trans></Button>
            </div> : null}
        </section>)}
        {loading ? <div className="flex justify-center p-3"><Spinner aria-label={t`Loading questions…`} /></div> : null}
        {!loading && !error && !items.length ? <p className="py-8 text-center text-sm text-muted-foreground"><Trans>You’re all caught up.</Trans></p> : null}
      </div>
      {retireError && current && retireError.messageId === current.item.message.id ? <p role="alert" className="border-t border-border px-5 py-2 text-sm text-destructive">{retireError.text}</p> : null}
      {navigation.length ? <nav aria-label={t`Question navigation`} className="flex items-center justify-between border-t border-border px-5 py-3">
        <Button variant="outline" size="sm" disabled={activeIndex === 0} onClick={() => goTo(activeIndex - 1)}><ChevronLeft size={16} aria-hidden="true" /><Trans>Previous</Trans></Button>
        <Button variant="ghost" size="sm" disabled={Boolean(retiringId)} onClick={() => void retireCurrent()} aria-label={t`Retire question`}><Trans>Retire question</Trans></Button>
        <Button variant="outline" size="sm" disabled={activeIndex >= navigation.length - 1} onClick={() => goTo(activeIndex + 1)}><Trans>Next</Trans><ChevronRight size={16} aria-hidden="true" /></Button>
      </nav> : null}
    </DialogContent>
  </Dialog>;
}
